

















































import { HTTPResponseCode } from '@/libs/HTTPResponseCodes'
import { computed, defineComponent, reactive, ref } from '@vue/composition-api'
import { domain, regex } from '@/composable/validator/rules'
import { useApi } from '@/composable/useApi'
import { useCrudChecker } from '@/composable/useCrudChecker'
import { useI18n } from 'vue-i18n-bridge'
import { useRoute } from '@/composable/migrateUseRoute'
import { useRouter } from '@intlify/vue-router-bridge'
import { useToasts } from '@/composable/useToasts'
import { useValidator } from '@/composable/validator/useValidator'
import CustomerActionDelete from '@/components/domains/customers/customer/actions/CustomerActionDelete.vue'
import CustomerSections from '@/components/domains/customers/customer/CustomerSections.vue'
import ResourceHeader from '@/components/layouts/dashboard/ResourceHeader.vue'
import ScrollableResource from '@/components/layouts/dashboard/ScrollableResource.vue'
import type { Customer } from '@futureecom/futureecom-js/dist/services/customer-service'
import type { RuleDefinitions, Rules } from '@/types/validator'

export default defineComponent({
  name: 'CustomerUpdate',

  components: {
    CustomerSections,
    ResourceHeader,
    CustomerActionDelete,
    ScrollableResource
  },

  setup() {
    const router = useRouter()
    const route = useRoute()

    const { t } = useI18n()
    const { addSuccessToast, addErrorToast } = useToasts()

    const { canCreate, canUpdate, canDelete } = useCrudChecker('customers\\customer')

    const api = useApi()

    const { id } = route.params

    const loading = reactive({
      get: true,
      update: false,
      delete: false
    })

    const item = ref<Customer>()

    const { errors, validate, isValid, setErrorsFromAPI, setModel } = useValidator()

    const rules = computed((): RuleDefinitions => {
      if (!item.value) {
        return {}
      }

      const nameRules: Rules = [
        'required',
        (val) => (!regex(val, /[^.' \p{L}\p{N}\p{M}-]/gu) ? 'Value cannot contain special characters' : ''),
        (val) => (!domain(val) ? 'Value cannot be URL' : ''),
        'max:64'
      ]

      return {
        firstname: nameRules,
        lastname: nameRules,
        email: ['required', 'email']
      }
    })

    const currentSection = computed({
      get: () => route.params.option || 'details',
      set: (option) => router.push({ params: { option } }).catch(Boolean)
    })

    const onItemChange = () => item.value && validate(item.value, rules.value)

    const redirectToGrid = () => router.push({ name: 'customers' })

    const onRequestError = (err: any) => {
      const { status, data } = err.response || {}
      if (status === HTTPResponseCode.NOT_FOUND) {
        addErrorToast(t('Resource not found'))
        canCreate.value ? router.push({ name: 'customer-create' }) : redirectToGrid()
        return
      }
      if (status === HTTPResponseCode.UNPROCESSABLE_CONTENT) {
        setErrorsFromAPI(data.errors || {})
      }
      addErrorToast(data.message)
    }

    const updateHandler = () => {
      if (!item.value) {
        return
      }

      validate(item.value, rules.value, true)

      if (!isValid.value) {
        return
      }

      loading.update = true

      api.customers
        .updateCustomer(`${id}`, item.value)
        .then((val) => {
          addSuccessToast(t('Updated customer'))
          item.value = val
        })
        .catch((err) => onRequestError(err))
        .finally(() => (loading.update = false))
    }

    api.customers
      .getCustomer(`${id}`)
      .then((val) => {
        item.value = val
        setModel(item.value)
      })
      .catch((err) => onRequestError(err))
      .finally(() => (loading.get = false))

    return {
      canUpdate,
      canDelete,
      item,
      errors,
      isValid,
      loading,
      currentSection,
      updateHandler,
      redirectToGrid,
      onItemChange,
      onRequestError
    }
  }
})
